import React, { useMemo, useState } from 'react';
import { Button, Card, Select, Space, Table, Tag, Typography } from 'antd';
import { BranchesOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import workOrderService from '@/services/workOrder.service';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';

const { Title, Text } = Typography;

type Row = {
  id: string;
  deviation_number: string;
  work_order_id: string;
  work_order_number?: string | null;
  status: string;
  justification_reason?: string | null;
  created_at?: string;
  generated_purchase_requisition_id?: string | null;
  pr_number?: string | null;
};

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'Draft', label: 'Draft' },
  { value: 'PendingApproval', label: 'Pending approval' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const InlandProcessDeviationsPage: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState('');

  const { data: rows = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['inland-process-deviations', 'sales-list', statusFilter],
    queryFn: () =>
      workOrderService.listInlandProcessDeviations({
        status: statusFilter || undefined,
      }),
  });

  const breadcrumbSegments = useMemo(
    () => [
      { title: 'Growth & Revenue', path: '/growth/dashboard' },
      { title: 'Inland process deviations' },
    ],
    []
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  const columns: ColumnsType<Row> = useMemo(
    () => [
      {
        title: 'Deviation #',
        dataIndex: 'deviation_number',
        key: 'deviation_number',
        render: (v: string, r) => (
          <Link to={`/growth/inland-process-deviations/${r.id}`}>{v || r.id}</Link>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (s: string) => <Tag>{s}</Tag>,
      },
      {
        title: 'Work order',
        dataIndex: 'work_order_id',
        key: 'work_order_id',
        render: (woId: string, r: Row) => {
          if (!woId) return '—';
          const label = r.work_order_number?.trim() || 'Open';
          return <Link to={`/mfg/work-orders/${woId}`}>{label}</Link>;
        },
      },
      {
        title: 'Justification',
        dataIndex: 'justification_reason',
        key: 'justification_reason',
        ellipsis: true,
        render: (t: string | null | undefined) => (t ? String(t) : '—'),
      },
      {
        title: 'PR',
        dataIndex: 'generated_purchase_requisition_id',
        key: 'pr',
        width: 100,
        render: (prId: string | null | undefined, r: Row) =>
          prId ? (
            <Link to={`/scm/procurement/purchase-requests/${prId}`}>
              {r.pr_number?.trim() || 'Open'}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        title: 'Created',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        render: (d: string | undefined) => (d ? dayjs(d).format('YYYY-MM-DD HH:mm') : '—'),
      },
    ],
    []
  );

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 8 }}>
        <BranchesOutlined /> Inland process deviations
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Requests to move in-house BOQ operations to outsourced processing (approval and linked PR).
      </Text>
      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            style={{ minWidth: 200 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_OPTIONS}
          />
          <Button type="link" icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>
            Refresh
          </Button>
        </Space>
        <Table<Row>
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={rows}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        />
      </Card>
    </div>
  );
};

export default InlandProcessDeviationsPage;
